import { useInfiniteQuery } from '@tanstack/react-query'
import { Empty, Flex, Select, Spin } from 'antd'
import { SelectProps } from 'antd/lib'
import { useState } from 'react'

import { useCounterpartServiceGetInternalInstitutionListEndpointKey } from 'src/api/queries'
import {
  CounterpartService,
  GetInternalInstitutionListResponseItem,
  GetInternationalNameListResponse,
  GetSubstanceListResponseItem,
  LegalEntityStatus
} from 'src/api/requests'
import { DEFAULT_PER_PAGE } from 'src/app-constants'
import { useDebounce } from 'src/hooks'
import { useLocales } from 'src/locales'

type Props = {
  excludeIds?: string[]
  apiStatus?: LegalEntityStatus
  includeAll?: boolean
  /** Pre-loaded options (e.g. from getInternalInstitutionEndpoint) so the select can show the value before list load. */
  initialOptions?: { value: string; label: string }[]
  onItemSelect?: (
    item: GetInternalInstitutionListResponseItem | undefined
  ) => void
} & SelectProps

export const InternalInstitutionsSelect = ({
  excludeIds,
  apiStatus,
  includeAll,
  initialOptions,
  onItemSelect,
  ...props
}: Props) => {
  const { t } = useLocales()
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search)

  const { data, fetchNextPage, isFetching } = useInfiniteQuery({
    queryKey: [
      useCounterpartServiceGetInternalInstitutionListEndpointKey,
      debouncedSearch
    ],
    queryFn: async ({ pageParam = 1 }: { pageParam?: number }) => {
      const response =
        await CounterpartService.getInternalInstitutionListEndpoint(
          pageParam,
          DEFAULT_PER_PAGE,
          undefined,
          debouncedSearch || undefined,
          undefined,
          apiStatus
        )

      return {
        data: response?.data ?? [],
        total_pages: response.totalPages ?? 0,
        current_page: pageParam
      }
    },
    getNextPageParam: (lastPage: {
      data: GetInternationalNameListResponse
      total_pages: number
      current_page: number
    }) =>
      lastPage?.current_page < lastPage?.total_pages
        ? lastPage.current_page + 1
        : undefined,
    initialPageParam: 1
  })

  const items = (data?.pages.flatMap((page) => page.data ?? []) ??
    []) as GetInternalInstitutionListResponseItem[]

  const listOptions = items.map((item: GetSubstanceListResponseItem) => ({
    value: item?.id,
    label: item?.name
  }))

  const initialValues = new Set(initialOptions?.map((o) => o.value) ?? [])
  const excludeSet = new Set(excludeIds?.map(String) ?? [])
  const listOptionsFiltered = listOptions.filter(
    (o) =>
      !initialValues.has(String(o?.value)) && !excludeSet.has(String(o?.value))
  )
  const options = [
    ...(includeAll ? [{ value: '', label: t('common.all') }] : []),
    ...(initialOptions ?? []),
    ...listOptionsFiltered
  ]

  return (
    <Select
      allowClear
      popupMatchSelectWidth={true}
      filterOption={false}
      options={options}
      onSearch={(value) => setSearch(value)}
      className="full-width"
      loading={isFetching}
      placeholder={t('common.institution')}
      showSearch={true}
      onPopupScroll={(e) => {
        const target = e.target as HTMLElement
        if (target.scrollTop + target.offsetHeight === target.scrollHeight)
          fetchNextPage()
      }}
      notFoundContent={
        props.loading ? (
          <Flex justify="center">
            <Spin />
          </Flex>
        ) : (
          <Empty />
        )
      }
      {...props}
      onClear={() => {
        setSearch('')
        onItemSelect?.(undefined)
        props.onClear?.()
      }}
      onChange={(value, option) => {
        const found = items.find((item) => item.id === value)
        onItemSelect?.(found)
        props.onChange?.(value, option)
      }}
    />
  )
}
